import React, { useState, useEffect } from 'react';
import axios from 'axios';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import {
  styled,
  Button,
  Container,
  TextField,
  Table,
  Modal,
  TableBody,
  TableCell,
  Box,
  Typography,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  IconButton,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  KeyboardDoubleArrowLeft,
  KeyboardDoubleArrowRight,
  Close as CloseIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const CustomTableScrollbar = styled('div')(({ theme }) => ({
  '&::-webkit-scrollbar': {
    height: '6px',
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
  },
  scrollbarWidth: 'thin',
  scrollbarColor: `${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'} transparent`,
}));

const Branch = () => {
  const theme = useTheme();
  const navigate = useNavigate();
 const storedUser = JSON.parse(
  sessionStorage.getItem('user') || localStorage.getItem('user') || '{}'
);
const isClientAdmin = storedUser?.role === 'client_admin';

// Organization dropdown (client admin only)
const [organizations, setOrganizations] = useState([]);
const [selectedOrgId, setSelectedOrgId] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const rowsPerPageOptions = [5, 10, 25, 50];
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showUserGuideModal, setShowUserGuideModal] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState({ BranchId: null, BranchName: '', isMainBranch: false });

  const [branches, setBranches] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isMainBranchAdmin = Boolean(
  JSON.parse(
    sessionStorage.getItem('user') || localStorage.getItem('user') || '{}'
  )?.is_main_branch_admin
);

const fetchOrganizations = async () => {
  try {
    const accessToken = localStorage.getItem('accessToken');
    const response = await axios.get(`${API_BASE_URL}/api/master/organizations/`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const orgs = (Array.isArray(response.data) ? response.data : response.data?.results || [])
      .filter((o) => o.is_active !== false);

    setOrganizations(orgs);
    if (orgs.length) setSelectedOrgId(String(orgs[0].id)); // default to first org
  } catch (err) {
    setSnackbarSeverity('error');
    setSnackbarMessage('Failed to fetch organizations.');
    setOpenSnackbar(true);
  }
};

   // Fetch branches for the logged-in user's organization only.
 const fetchBranches = async () => {
  // Client admin: wait until an org is selected from the dropdown
  if (isClientAdmin && !selectedOrgId) return;

  setLoading(true);
  try {
    const accessToken = localStorage.getItem('accessToken');
    const loggedInBranchId = storedUser?.branch_id || null;

    // Client admin → selected org; everyone else → their own org
    const organizationId = isClientAdmin
      ? selectedOrgId
      : storedUser?.organization_id || '';

    const response = await axios.get(`${API_BASE_URL}/api/master/branch/`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      params: organizationId ? { organization_id: organizationId } : {},
    });

    // Handle both array response and {results, branches} response styles.
    const rawData = Array.isArray(response.data)
      ? response.data
      : response.data?.results || response.data?.branches || [];

    // Safety net in case the API ignores the organization_id param
    const orgScoped = organizationId
      ? rawData.filter((b) => String(b.organization) === String(organizationId))
      : rawData;

    const mappedBranches = orgScoped.map((branch) => ({
      BranchId: branch.id,
      BranchName: branch.name,
      email: branch.email,
      mobile: branch.mobile,
      landline: branch.landline || '',
      address: branch.address || '',
      organization: branch.organization,
      organizationName: branch.organization_name || '',
      country: branch.country,
      countryName: branch.country_name || '',
      state: branch.state,
      stateName: branch.state_name || '',
      city: branch.city,
      cityName: branch.city_name || '',
      zipcode: branch.zipcode || '',
      tin_number: branch.tin_number || '',
      affiliation_number: branch.affiliation_number || '',
      fax: branch.fax || '',
      website: branch.website || '',
      bank_name: branch.bank_name || '',
      bank_branch: branch.bank_branch || '',
      account_holder: branch.account_holder || '',
      account_number: branch.account_number || '',
      ifsc_code: branch.ifsc_code || '',
      gst_number: branch.gst_number || '',
      image: branch.image || null,
      is_main_branch: branch.is_main_branch || false,
      is_active: branch.is_active !== undefined ? branch.is_active : true,
    }));

    // Client admin and main-branch admin see all branches of the org;
    // other users see only their own branch.
    const canSeeAll = isClientAdmin || isMainBranchAdmin;
    const visibleBranches = canSeeAll
      ? mappedBranches
      : mappedBranches.filter(
          (branch) => String(branch.BranchId) === String(loggedInBranchId)
        );

    setBranches(visibleBranches);
    setFilteredData(visibleBranches);
    setPage(1);
  } catch (err) {
    setError(err.message);
    setSnackbarSeverity('error');
    setSnackbarMessage('Failed to fetch branches.');
    setOpenSnackbar(true);
    if (err.response?.status === 401) {
      localStorage.removeItem('accessToken');
      navigate('/login');
    }
  } finally {
    setLoading(false);
  }
}; 

  // Delete a branch
  const handleDelete = async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');

      if (!selectedBranch?.BranchId) {
        throw new Error('Branch ID is missing.');
      }

      await axios.delete(
        `${API_BASE_URL}/api/master/branch/${selectedBranch.BranchId}/`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      const updatedBranches = branches.filter((branch) => branch.BranchId !== selectedBranch.BranchId);
      setBranches(updatedBranches);
      setFilteredData(updatedBranches);
      setShowModal(false);
      setSnackbarMessage('Branch deleted successfully!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      // Refresh to get updated main branch status
      fetchBranches();
    } catch (err) {
      setError(err.message);
      setSnackbarSeverity('error');
      setSnackbarMessage('Failed to delete branch.');
      setOpenSnackbar(true);
      if (err.response?.status === 401) {
        localStorage.removeItem('accessToken');
        navigate('/login');
      }
    }
  };

  const handleAddBranch = () => {
    navigate('/add-branch');
  };

  const handleEdit = (BranchId) => {
    navigate(`/edit-branch/${BranchId}`);
  };

  const handleClose = () => {
    setShowModal(false);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

 // On mount: auth check, and load orgs for client admin
useEffect(() => {
  const accessToken = localStorage.getItem('accessToken');
  if (!accessToken) {
    navigate('/login');
    return;
  }
  if (isClientAdmin) fetchOrganizations();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [navigate]);

// Load branches: immediately for normal users, on org change for client admin
useEffect(() => {
  if (!localStorage.getItem('accessToken')) return;
  fetchBranches();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [selectedOrgId]);

  useEffect(() => {
    const filtered = branches.filter((branch) =>
      branch.BranchName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      branch.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      branch.mobile.toLowerCase().includes(searchTerm.toLowerCase()) ||
      branch.address.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredData(filtered);
  }, [searchTerm, branches]);

  const paginatedData = filteredData.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  return (
    <Container>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
          p: 2,
          boxShadow: 'none',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
          <TextField
            variant="outlined"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{
                      color: theme.palette.primary.main,
                      marginLeft: 1.5,
                      fontSize: 24,
                    }}
                  />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchTerm('')}
                    sx={{
                      color: "grey",
                      marginRight: 1,
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
              sx: {
                borderRadius: '30px',
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.15)'
                  : 'rgba(255, 255, 255, 1)',
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 2px 8px rgba(0, 0, 0, 0.3)'
                  : '0 2px 8px rgba(0, 0, 0, 0.1)',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`,
                },
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.2)'
                    : 'rgba(240, 240, 240, 1)',
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 2px 12px rgba(0, 0, 0, 0.4)'
                    : '0 2px 12px rgba(0, 0, 0, 0.15)',
                },
                '&.Mui-focused': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.25)'
                    : 'rgba(255, 255, 255, 1)',
                  boxShadow: `0 0 0 3px ${theme.palette.primary.main}20`,
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: `2px solid ${theme.palette.primary.main}`,
                  },
                },
                paddingLeft: 1.5,
                height: '50px',
                transition: 'all 0.3s ease',
              },
            }}
            sx={{
              width: 350,
              maxWidth: '100%',
              '& .MuiInputBase-input': {
                padding: '10px 14px',
                color: theme.palette.text.primary,
                fontWeight: 500,
              },
            }}
          />

          {isClientAdmin && organizations.length > 0 && (
  <FormControl
    size="small"
    sx={{
      minWidth: 220,
      '& .MuiOutlinedInput-root': {
        height: '50px',
        borderRadius: '30px',
        backgroundColor: theme.palette.background.paper,
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.primary.main,
          borderWidth: '2px',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.primary.main,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.primary.main,
        },
      },
      '& .MuiInputLabel-root': {
        color: theme.palette.primary.main,
        '&.Mui-focused': { color: theme.palette.primary.main },
      },
    }}
  >
    <InputLabel id="branch-page-org-label">Organization</InputLabel>
    <Select
      labelId="branch-page-org-label"
      value={selectedOrgId}
      label="Organization"
      onChange={(e) => setSelectedOrgId(e.target.value)}
      sx={{ fontWeight: 500 }}
    >
      {organizations.map((org) => (
        <MenuItem key={org.id} value={String(org.id)}>
          {org.name || `Organization ${org.id}`}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
)}

          {(isMainBranchAdmin || isClientAdmin)  && (
  <Button
    variant="contained"
    onClick={handleAddBranch}
    sx={{
      ml: 2,
      width: '170px',
      borderRadius: '10px',
      height: '50px',
      padding: '10px 0',
      fontWeight: 'bold',
      backgroundColor: theme.palette.primary.main,
      '&:hover': { backgroundColor: theme.palette.primary.dark },
    }}
    startIcon={<AddIcon />}
  >
    ADD BRANCH
  </Button>
)}
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <IconButton
            onClick={() => setShowUserGuideModal(true)}
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              minWidth: 0,
            }}
          >
            <InfoOutlineIcon sx={{ fontSize: 30 }} />
          </IconButton>
        </Box>
      </Box>

      <Paper
        elevation={3}
        sx={{
          paddingBottom: 0,
          maxWidth: '100%',
          boxSizing: 'border-box',
          mx: 'auto',
          backgroundColor: theme.palette.background.paper,
          borderRadius: '16px',
        }}
      >
        <CustomTableScrollbar sx={{ width: '100%', overflowX: 'auto' }}>
          <TableContainer sx={{ width: '100%', borderRadius: '16px', backgroundColor: theme.palette.background.paper }}>
            <Table
              sx={{
                '& .MuiTableHead-root .MuiTableCell-root': {
                  fontWeight: 'bold',
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  fontSize: '1.1rem',
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Branch Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Email-Id</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Mobile No</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Address</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Type</TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 'bold',
                      fontSize: '1.1rem',
                      backgroundColor: theme.palette.mode === 'dark' ? '#064b7a' : theme.palette.primary.dark,
                      color: theme.palette.primary.contrastText,
                    }}
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : paginatedData.length > 0 ? (
                  paginatedData.map((Branch, index) => (
                    <TableRow
                      key={Branch.BranchId}
                      sx={{
                        backgroundColor: index % 2 === 0 ? theme.palette.background.paper : theme.palette.background.paper,
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                    >
                      <TableCell>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {Branch.BranchName}
                        </Box>
                      </TableCell>
                      <TableCell>{Branch.email}</TableCell>
                      <TableCell>{Branch.mobile}</TableCell>
                      <TableCell
                        sx={{
                          maxWidth: '200px',
                          wordWrap: 'break-word',
                          whiteSpace: 'normal',
                        }}
                      >
                        {Branch.address}
                      </TableCell>
                      <TableCell>
                        {Branch.is_main_branch ? (
                          <Chip
                            icon={<StarIcon sx={{ fontSize: '14px !important' }} />}
                            label="Main"
                            size="small"
                            sx={{
                              backgroundColor: theme.palette.warning.light,
                              color: theme.palette.text.primary,
                              fontWeight: 'bold',
                              fontSize: '0.75rem',
                            }}
                          />
                        ) : (
                          <Chip
                            label="Sub"
                            size="small"
                            sx={{
                              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
                              color: theme.palette.text.disabled,
                              fontSize: '0.75rem',
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <span>
                          <IconButton
                            onClick={() => handleEdit(Branch.BranchId)}
                            sx={{
                              color: theme.palette.primary.light,
                              '&:hover': {
                                backgroundColor: theme.palette.action.hover,
                              },
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        </span>
                        <span>
                          <IconButton
                            color="error"
                            onClick={() => {
                              setSelectedBranch({
                                BranchId: Branch.BranchId,
                                BranchName: Branch.BranchName,
                                isMainBranch: Branch.is_main_branch,
                              });
                              setShowModal(true);
                            }}
                            sx={{
                              '&:hover': {
                                backgroundColor: theme.palette.action.hover,
                              },
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </span>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow sx={{
                    backgroundColor: theme.palette.background.paper,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}>
                    <TableCell colSpan={7} align="center" sx={{ fontSize: '1rem', padding: '20px', wordSpacing: 7 }}>
                      No Data Found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CustomTableScrollbar>
      </Paper>

      {filteredData.length > 0 && (
        <Box
          sx={{
            maxWidth: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 1,
            p: 2,
            mx: 'auto',
            pt: 2,
          }}
        >
          <Typography variant="body2" color={theme.palette.primary.main}>
            Page <strong>{page}</strong> of <strong>{Math.ceil(filteredData.length / rowsPerPage)}</strong>
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton
              onClick={() => handleChangePage(null, page - 1)}
              disabled={page === 1}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
                borderRadius: '50%',
                width: '36px',
                height: '36px',
              }}
            >
              <KeyboardDoubleArrowLeft />
            </IconButton>
            {[...Array(Math.ceil(filteredData.length / rowsPerPage))].map((_, index) => (
              <IconButton
                key={index + 1}
                onClick={() => handleChangePage(null, index + 1)}
                sx={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  backgroundColor: page === index + 1 ? theme.palette.primary.main : 'transparent',
                  color: page === index + 1 ? theme.palette.primary.contrastText : theme.palette.text.primary,
                  '&:hover': {
                    backgroundColor: page === index + 1 ? theme.palette.primary.dark : theme.palette.action.hover,
                  },
                }}
              >
                {index + 1}
              </IconButton>
            ))}
            <IconButton
              onClick={() => handleChangePage(null, page + 1)}
              disabled={page >= Math.ceil(filteredData.length / rowsPerPage)}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
                borderRadius: '50%',
                width: '36px',
                height: '36px',
              }}
            >
              <KeyboardDoubleArrowRight />
            </IconButton>
            <FormControl size="small" sx={{ minWidth: 100, ml: 1 }}>
              <InputLabel id="rows-per-page-label" sx={{ color: theme.palette.text.primary }}>
                Rows
              </InputLabel>
              <Select
                labelId="rows-per-page-label"
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}
                label="Rows"
                sx={{
                  height: '36px',
                  color: theme.palette.text.primary,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.text.primary,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                {rowsPerPageOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Box>
      )}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Modal
        open={showModal}
        onClose={handleClose}
        aria-labelledby="delete-modal-title"
        aria-describedby="delete-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: theme.palette.background.paper,
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px' }}>
            <Typography id="delete-modal-title" variant="h6" sx={{ color: theme.palette.primary.contrastText }}>
              Delete Confirmation ⚠️
            </Typography>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <Typography id="delete-modal-description" sx={{ mb: 2 }}>
              Are you sure you want to delete <strong>{selectedBranch.BranchName}</strong>?
            </Typography>
            {selectedBranch.isMainBranch && (
              <Typography variant="body2" sx={{ mb: 2, color: theme.palette.warning.main, fontWeight: 500 }}>
                ⚠️ This is the Main Branch. Deleting it will shift the main branch status to the previous main branch.
              </Typography>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button onClick={handleClose} variant="outlined" sx={{ mr: 1 }} data-app-cancel="true">
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleDelete}
              >
                Yes, Delete
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* User Guide Modal */}
      <Modal
        open={showUserGuideModal}
        onClose={() => setShowUserGuideModal(false)}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 900,
            bgcolor: 'background.paper',
            borderRadius: '16px',
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              backgroundColor: theme.palette.primary.main,
              padding: '12px 16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'relative',
            }}
          >
            <Typography variant="h4" sx={{ color: '#fff', fontWeight: 'bold' }}>
              Branch Management - User Guide
            </Typography>
            <IconButton
              onClick={() => setShowUserGuideModal(false)}
              sx={{ color: '#fff', position: 'absolute', right: 14 }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <CustomTableScrollbar sx={{ maxHeight: '70vh', overflowY: 'auto', padding: '20px' }}>
            <Typography
              variant="body1"
              sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1 }}
            >
              ➤ Follow these steps to add a new branch:
            </Typography>
            <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
              <li>
                Click the <strong>"ADD BRANCH"</strong> button to navigate to the Add Branch page.
              </li>
              <li>
                Fill in all the <strong>required fields</strong> (marked with *).
              </li>
              <li>
                Optionally, upload a <strong>branch image</strong> if needed.
              </li>
              <li>
                Toggle <strong>"Set as Main Branch"</strong> if this branch should be the main branch.
              </li>
              <li>
                Click <strong>"SAVE"</strong> to create the branch. A success notification will confirm the addition along with the generated login credentials.
              </li>
            </ul>

            <Typography
              variant="body1"
              sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
            >
              ➤ Follow these steps to update an existing branch:
            </Typography>
            <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
              <li>
                Locate the branch you want to update in the Branch List table.
              </li>
              <li>
                Click the <strong>Edit</strong> icon (pencil) in the Action column.
              </li>
              <li>
                Update the fields as needed.
              </li>
              <li>
                If changing the <strong>Main Branch</strong> status, a confirmation dialog will appear.
              </li>
              <li>
                Click <strong>"UPDATE"</strong> to save your changes. A success notification will confirm the update.
              </li>
            </ul>

            <Typography
              variant="body1"
              sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
            >
              ➤ Follow these steps to delete a branch:
            </Typography>
            <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
              <li>
                Find the branch you want to delete in the Branch List table.
              </li>
              <li>
                Click the <strong>Delete</strong> icon (trash) in the Action column.
              </li>
              <li>
                Confirm the deletion in the confirmation dialog. The branch will be removed, and a success notification will appear.
              </li>
              <li>
                If the deleted branch was the Main Branch, the status will automatically shift to the previous main branch.
              </li>
            </ul>

            <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
              ⚠️ Note: Deleted branches cannot be recovered. Proceed with caution.
            </Typography>
          </CustomTableScrollbar>
        </Box>
      </Modal>
    </Container>
  );
};

export default Branch;